import type { RecommendationOutfitCarousel, RecommendationResponse } from '@/data/recom';
import valueIsArray from './typeAssertions/valueIsArray';
import valueIsRecord from './typeAssertions/valueIsRecord';
import valueIsString from './typeAssertions/valueIsString';

type IARecommendationType = 'recommendation' | 'outfit' | 'complements' | 'sameBrand' | 'family';

const LIST_FIELD_BY_TYPE: Record<Exclude<IARecommendationType, 'outfit'>, string> = {
	recommendation: 'recommendations',
	complements: 'complements',
	sameBrand: 'sameBrand',
	family: 'family',
};

const idsOf = (list: unknown): string[] => {
	if (!valueIsArray(list)) {
		return [];
	}

	return list.map((item) => (valueIsRecord(item) ? item?.id : undefined)).filter(valueIsString);
};

/**
 * El wrapper devuelve `outfits` como carruseles (ancla + piezas por zona), no
 * como la lista plana del servicio anterior. Se toma el primero, que es el look
 * armado alrededor del producto consultado.
 */
const outfitIds = (outfits: unknown): string[] => {
	if (!valueIsArray(outfits)) {
		return [];
	}

	const [carousel] = outfits as RecommendationOutfitCarousel[];
	if (!carousel) {
		return [];
	}

	const ids = [carousel.anchor?.id, ...idsOf(carousel.items)].filter(valueIsString);
	return Array.from(new Set(ids));
};

export const partNumbersIARecommendationExtractorByUnknown = (
	type: IARecommendationType,
	data: unknown
): string[] => {
	if (!valueIsRecord(data)) {
		return [];
	}

	const innerData = (data as RecommendationResponse).data;
	if (!valueIsRecord(innerData)) {
		return [];
	}

	const inner = innerData as Record<string, unknown>;

	if (type === 'outfit') {
		return outfitIds(inner.outfits);
	}

	return idsOf(inner[LIST_FIELD_BY_TYPE[type]]);
};

const outfitsOf = (data: unknown): RecommendationOutfitCarousel[] => {
	const innerData = valueIsRecord(data) ? (data as RecommendationResponse).data : undefined;
	const outfits = valueIsRecord(innerData)
		? (innerData as Record<string, unknown>).outfits
		: undefined;
	return valueIsArray(outfits) ? (outfits as RecommendationOutfitCarousel[]) : [];
};

export const allOutfitPartNumbers = (data: unknown): string[] =>
	Array.from(new Set(outfitsOf(data).flatMap((outfit) => idsOf(outfit?.items))));

export const selectMostAvailableOutfit = <T extends { partNumber: string }>(
	products: T[],
	data: unknown
): T[] => {
	const byPartNumber = new Map(products.map((product) => [product.partNumber, product]));
	const [best] = outfitsOf(data)
		.map((outfit, index) => ({
			index,
			score: typeof outfit?.score === 'number' ? outfit.score : 0,
			products: idsOf(outfit?.items).flatMap((id) => {
				const product = byPartNumber.get(id);
				return product ? [product] : [];
			}),
		}))
		.sort(
			(a, b) => b.products.length - a.products.length || b.score - a.score || a.index - b.index
		);
	return best?.products ?? [];
};
